import React, { useEffect, useState } from 'react';
import { Button, Result, Space, Spin } from 'antd';
import {
  getCurrentSession,
  buildLoginRedirectUrl,
  getLoginUrl,
  logoutSession,
} from '../api/auth';
import { AuthProvider } from '../context/AuthContext';
import type { Session } from '../api/auth';
import { hasKacAccess } from '../utils/permissions';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [denied, setDenied] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const currentSession = await getCurrentSession();
        if (cancelled) return;
        if (!currentSession) {
          window.location.href = buildLoginRedirectUrl();
          return;
        }
        if (!hasKacAccess(currentSession.identity?.traits)) {
          setSession(currentSession);
          setDenied(true);
          return;
        }
        setSession(currentSession);
      } catch {
        if (!cancelled) {
          window.location.href = buildLoginRedirectUrl();
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goAuthConsole = () => {
    const loginUrl = getLoginUrl();
    const base = loginUrl.replace(/\/login\/?$/i, '');
    window.location.href = base || loginUrl;
  };

  const handleLogout = async () => {
    const loginUrl = getLoginUrl();
    setLoggingOut(true);
    try {
      await logoutSession(loginUrl);
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = loginUrl;
    } finally {
      setLoggingOut(false);
    }
  };

  if (checking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="正在校验会话..." />
      </div>
    );
  }

  if (denied) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Result
          status="403"
          title="无访问权限"
          subTitle="当前账号没有财务中心（KAC）权限（需要 koc_finance 或 super_admin）。请联系管理员。"
          extra={
            <Space>
              <Button type="primary" onClick={goAuthConsole}>
                打开认证控制台
              </Button>
              <Button loading={loggingOut} onClick={handleLogout}>
                退出登录
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return <AuthProvider session={session}>{children}</AuthProvider>;
};

export default AuthGuard;
